import { Paths } from "@app/routes";
import { ConfirmAction } from "@atlas/composite";
import {
  Button,
  CardContent,
  CommandSelectComponent,
  cn,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  type PaginationState,
  ScrollArea,
  TabsContent,
  useToast,
  WithToolTip,
} from "@atlas/core";
import { FilterBuilder, useSessionData, useTranslation } from "@atlas/sdk";
import { IHubPackageVisibility } from "@enums";
import type { IHubPackage } from "@models";
import {
  useDeleteHubCart,
  useDeleteHubPackage,
  useExportHubPackages,
  useHubCurrentCart,
  useSearchHubPackages,
  useUpdateHubPackage,
} from "@query";
import { defaultHubCartPackageId } from "@shared";
import {
  ExportTokenDialog,
  ImportTokenDialog,
  PackageCard,
} from "@shared/components";
import { useCreatePackageSave, useSwitchPackage } from "@shared/hooks";
import {
  CircleCheckBig,
  Download,
  Plus,
  ShoppingCart,
  Trash2,
  Upload,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CatalogPagination } from "../../catalog/pages/catalog/CatalogPagination";
import { EditPackageDialog } from "./EditPackageDialog";

function catalogCount(pkg: IHubPackage): number {
  return pkg.catalogRelations?.length ?? 0;
}

export function PackagesTab() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();

  const filter = useMemo(() => {
    const builder = new FilterBuilder();
    if (searchQuery.trim()) {
      builder.contains("label", searchQuery.trim());
    }
    return builder.build();
  }, [searchQuery, uid]);

  const {
    data: packagesData,
    isLoading,
    isFetching,
    refetch,
  } = useSearchHubPackages({
    filter,
    sort: ["modifiedByDatetime DESC"],
    mode: "DATA",
    startIndex: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
  });

  const { data: userHubCart, isLoading: userHubCartsLoading } =
    useHubCurrentCart(uid, { enabled: uid.length > 0 });
  const defaultCartPackageId = useMemo(
    () => defaultHubCartPackageId(userHubCart),
    [userHubCart],
  );

  const { mutate: deleteHubCart, isPending: isDeleteHubCartPending } =
    useDeleteHubCart();

  const hubCartBusy = isDeleteHubCartPending || userHubCartsLoading;

  const { switchPackage, isSwitchPending } = useSwitchPackage(userHubCart);

  const packages = useMemo(() => {
    const allPackages =
      packagesData?.pages.flatMap(
        (page: { items: IHubPackage[] }) => page.items,
      ) ?? [];

    return allPackages.filter((pkg) => {
      // If visibility is private, only show if the current user created it
      if (pkg.visibility === IHubPackageVisibility.Private) {
        return pkg.createdBy === uid;
      }
      // Otherwise, show the package (Company or Platform visibility)
      return true;
    });
  }, [packagesData, uid]);

  const totalCount = useMemo(
    () => packagesData?.pages[0]?.totalCount ?? 0,
    [packagesData],
  );
  const { mutate: deletePackage } = useDeleteHubPackage();
  const { mutate: updatePackage, isPending: isUpdatePending } =
    useUpdateHubPackage();
  const { mutate: exportPackages, isPending: isExportPending } =
    useExportHubPackages();
  const [deleteTarget, setDeleteTarget] = useState<IHubPackage | null>(null);
  const [editTarget, setEditTarget] = useState<IHubPackage | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const { handleCreatePackageSave, isCreatePending } = useCreatePackageSave(
    setCreateOpen,
    {
      onSuccess: () => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        refetch();
      },
    },
  );
  const [selectedForExport, setSelectedForExport] = useState<Set<number>>(
    new Set(),
  );
  const [exportedKey, setExportedKey] = useState<string | null>(null);

  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const isLoadingPackages = useMemo(
    () =>
      isLoading ||
      isFetching ||
      isSwitchPending ||
      hubCartBusy ||
      isCreatePending,
    [isLoading, isFetching, isSwitchPending, hubCartBusy, isCreatePending],
  );

  const handleDeleteConfirm = useCallback(() => {
    const id = deleteTarget?.packageId;
    setDeleteTarget(null);
    if (id == null || id <= 0) return;
    deletePackage(id, {
      onSuccess: () => {
        if (userHubCart?.packageId === id && userHubCart.cartId != null) {
          deleteHubCart(userHubCart.cartId);
        }
        toast.add({
          title: t("hub.package_manager.delete_success"),
          data: { type: "success" },
        });
      },
      onError: (error: unknown) => {
        const apiError = error as { status?: number; message?: string };
        toast.add({
          title: t("hub.package_manager.delete_error"),
          ...(apiError.status === 409 && apiError.message
            ? { description: apiError.message }
            : {}),
          data: { type: "error" },
        });
      },
    });
  }, [deleteTarget, deletePackage, toast, t, userHubCart, deleteHubCart]);

  const handleDefaultCartSwitch = useCallback(
    (row: IHubPackage, checked: boolean) => {
      if (!checked) {
        if (userHubCart?.cartId != null) {
          deleteHubCart(userHubCart.cartId, {
            onSuccess: () => {
              refetch();
            },
          });
        }
        return;
      }
      switchPackage(row, {
        onSuccess: () => {
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          refetch();
        },
      });
    },
    [refetch, switchPackage, userHubCart?.cartId, deleteHubCart],
  );

  const handleFormSave = useCallback(
    (values: {
      label: string;
      description: string;
      visibility: IHubPackageVisibility;
    }) => {
      const source = editTarget;
      if (source != null) {
        if (source.packageId == null || source.packageId <= 0) return;
        const body: IHubPackage = {
          ...source,
          name: values.label,
          label: values.label,
          description: values.description,
          visibility: values.visibility,
          catalogRelations: source.catalogRelations ?? [],
        };
        updatePackage(body, {
          onSuccess: () => {
            setEditTarget(null);
            toast.add({
              title: t("hub.package_manager.edit_success"),
              data: { type: "success" },
            });
          },
          onError: (error: unknown) => {
            const apiError = error as { status?: number; message?: string };
            toast.add({
              title: t("hub.package_manager.edit_error"),
              ...(apiError.status === 409 && apiError.message
                ? { description: apiError.message }
                : {}),
              data: { type: "error" },
            });
          },
        });
        return;
      }

      // Defer to the shared hook for creation logic
      handleCreatePackageSave(values);
    },
    [editTarget, updatePackage, handleCreatePackageSave, toast, t],
  );

  const handleRowClick = useCallback(
    (row: IHubPackage) => {
      const pkgId = row.packageId;
      if (pkgId == null || pkgId <= 0) return;

      if (pkgId !== defaultCartPackageId) {
        handleDefaultCartSwitch(row, true);
      }

      navigate(`/${Paths.PackageManager}?packageId=${pkgId}&fromTab=packages`);
    },
    [navigate, defaultCartPackageId, handleDefaultCartSwitch],
  );

  const deleteLabel =
    deleteTarget?.label?.trim() ||
    deleteTarget?.name?.trim() ||
    t("hub.package_manager.delete_fallback_name");

  const handleExportToggle = useCallback((pkgId: number) => {
    setSelectedForExport((prev) => {
      const next = new Set(prev);
      if (next.has(pkgId)) {
        next.delete(pkgId);
      } else {
        next.add(pkgId);
      }
      return next;
    });
  }, []);

  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExportSubmit = useCallback(() => {
    if (selectedForExport.size === 0) return;

    setIsExportDialogOpen(true);

    // Build payload
    const packagesToExport = Array.from(selectedForExport);
    const catalogsToExport: { id: number; version: number }[] = [];

    for (const pkgId of packagesToExport) {
      const pkg = packages.find((p) => p.packageId === pkgId);
      if (pkg?.catalogRelations) {
        for (const rel of pkg.catalogRelations) {
          if (rel.catalogId && rel.catalogVersion) {
            catalogsToExport.push({
              id: rel.catalogId,
              version: rel.catalogVersion,
            });
          }
        }
      }
    }

    const payload = {
      packages: packagesToExport,
    };

    exportPackages(payload, {
      onSuccess: (response) => {
        toast.add({
          title: t("hub.package_manager.export_success", {
            defaultValue: "Export successful",
          }),
          data: { type: "success" },
        });
        setSelectedForExport(new Set());
        if (response?.data) {
          setExportedKey(response.data);
        }
      },
      onError: (error: unknown) => {
        const apiError = error as { status?: number; message?: string };
        setExportedKey(null);
        toast.add({
          title: t("hub.package_manager.export_error", {
            defaultValue: "Export failed",
          }),
          ...(apiError.status === 409 && apiError.message
            ? { description: apiError.message }
            : {}),
          data: { type: "error" },
        });
      },
    });
  }, [selectedForExport, packages, toast, t, exportPackages]);

  const packageCards = useMemo(() => {
    return packages.map((pkg: IHubPackage) => {
      const id = pkg.packageId;
      const canMutate = id != null && id > 0;
      const isActive =
        defaultCartPackageId > 0 && id != null && id === defaultCartPackageId;
      const isExportSelected = id != null && selectedForExport.has(id);

      return (
        <PackageCard
          key={id}
          pkg={pkg}
          catalogCount={catalogCount(pkg)}
          isActive={isActive}
          isExportSelected={isExportSelected}
          onClick={() => handleRowClick(pkg)}
          actions={
            <>
              <WithToolTip
                tooltipText={
                  isActive
                    ? t("hub.package_manager.remove_default_cart_aria", {
                        defaultValue: "Toggle as inactive",
                      })
                    : t("hub.package_manager.default_cart_aria", {
                        defaultValue: "Toggle as active",
                      })
                }>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!canMutate || hubCartBusy}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDefaultCartSwitch(pkg, !isActive);
                  }}
                  aria-label={
                    isActive
                      ? t("hub.package_manager.remove_default_cart_aria", {
                          defaultValue: "Toggle as inactive",
                        })
                      : t("hub.package_manager.default_cart_aria", {
                          defaultValue: "Toggle as active",
                        })
                  }>
                  <CircleCheckBig
                    className={cn(
                      "size-4",
                      isActive
                        ? "text-success-foreground"
                        : "text-muted-foreground/50",
                    )}
                  />
                </Button>
              </WithToolTip>
              <WithToolTip
                tooltipText={
                  catalogCount(pkg) === 0
                    ? t("hub.package_manager.export_no_catalogs", {
                        defaultValue: "No Catalogs in Package",
                      })
                    : isExportSelected
                      ? t("hub.package_manager.export_remove_aria", {
                          defaultValue: "Remove from export",
                        })
                      : t("hub.package_manager.export_select_aria", {
                          defaultValue: "Select for export",
                        })
                }>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!canMutate || catalogCount(pkg) === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canMutate && id != null) {
                      handleExportToggle(id);
                      if (isExportSelected) {
                        toast.add({
                          title: t(
                            "hub.package_manager.export_package_removed",
                            {
                              defaultValue: "Package removed from export list",
                            },
                          ),
                          data: { type: "default" },
                        });
                      } else {
                        toast.add({
                          title: t("hub.package_manager.export_package_added", {
                            defaultValue: "Package added to export list",
                          }),
                          data: { type: "success" },
                        });
                      }
                    }
                  }}
                  aria-label={t("hub.package_manager.export_select_aria", {
                    defaultValue: "Select for export",
                  })}>
                  <Upload
                    className={cn("size-4", isExportSelected && "text-primary")}
                  />
                </Button>
              </WithToolTip>
              <WithToolTip
                tooltipText={t("hub.package_manager.delete_aria", {
                  defaultValue: "Delete package",
                })}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  disabled={!canMutate}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canMutate) setDeleteTarget(pkg);
                  }}
                  aria-label={t("hub.package_manager.delete_aria", {
                    defaultValue: "Delete package",
                  })}>
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </WithToolTip>
            </>
          }
        />
      );
    });
  }, [
    packages,
    defaultCartPackageId,
    handleRowClick,
    hubCartBusy,
    handleDefaultCartSwitch,
    t,
    selectedForExport,
    handleExportToggle,
  ]);

  return (
    <>
      <TabsContent
        value="packages"
        className="flex-1 min-h-0 h-full flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <div className="relative w-full sm:w-auto">
            <CommandSelectComponent
              displayType="search"
              placeholder={t("hub.package_manager.search_placeholder")}
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              debounceMs={300}
              className="ml-1 w-stretch sm:w-72"
            />
          </div>
          <div className="flex items-center gap-2 sm:self-auto">
            {totalCount > 0 && (
              <CatalogPagination
                pageIndex={pagination.pageIndex}
                pageSize={pagination.pageSize}
                totalCount={totalCount}
                onPaginationChange={setPagination}
              />
            )}

            <WithToolTip
              tooltipText={
                selectedForExport.size > 0
                  ? t("hub.package_manager.export_selected", {
                      defaultValue: "Export selected packages",
                    })
                  : t("hub.package_manager.export_disabled", {
                      defaultValue: "Select packages to enable export",
                    })
              }>
              <div className="inline-block">
                <Button
                  type="button"
                  variant="outline"
                  className={cn(selectedForExport.size > 0 && "gap-1.5 px-3")}
                  onClick={handleExportSubmit}
                  disabled={isLoading || selectedForExport.size === 0}>
                  <Upload className="size-4" />
                  {selectedForExport.size > 0 && `(${selectedForExport.size})`}
                </Button>
              </div>
            </WithToolTip>
            <WithToolTip
              tooltipText={t("hub.package_manager.import_packages", {
                defaultValue: "Import packages",
              })}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setImportDialogOpen(true)}>
                <Download className="size-4" />
              </Button>
            </WithToolTip>

            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateOpen(true)}
              disabled={isLoading}>
              <Plus className="mr-2 size-4" />
              {t("hub.package_manager.create", { defaultValue: "New Package" })}
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          {packages.length === 0 && !isLoadingPackages ? (
            <Empty className="mx-auto min-h-0 max-w-3xl flex-1">
              <EmptyMedia>
                <ShoppingCart className="h-6 w-6 text-muted-foreground" />
              </EmptyMedia>
              <EmptyHeader>
                <EmptyTitle>
                  {t("hub.package_manager.packages_empty", {
                    defaultValue: "Your packages are empty.",
                  })}
                </EmptyTitle>
                <EmptyDescription>
                  {t("hub.package_manager.packages_empty_description", {
                    defaultValue:
                      "Explore the catalogs to add to your package.",
                  })}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="grid auto-rows-auto grid-cols-[repeat(auto-fill,345px)] gap-4 justify-start pb-4">
              {isLoadingPackages
                ? Array.from({ length: 6 }).map((_, i) => (
                    <CardContent
                      key={i}
                      isLoading={true}
                      className="h-65 w-85 rounded-xl"></CardContent>
                  ))
                : packageCards}
            </div>
          )}
        </ScrollArea>
      </TabsContent>

      <EditPackageDialog
        open={editTarget != null || createOpen}
        variant={editTarget != null ? "edit" : "create"}
        pkg={editTarget}
        onOpenChange={(open) => {
          if (!open) {
            setEditTarget(null);
            setCreateOpen(false);
          }
        }}
        isSubmitting={isUpdatePending || isCreatePending}
        onSave={handleFormSave}
      />

      <ConfirmAction
        open={deleteTarget != null}
        onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
        title={t("hub.package_manager.delete_title")}
        description={t("hub.package_manager.delete_description", {
          label: deleteLabel,
        })}
        confirmText={t("hub.catalog_manager.delete")}
        cancelText={t("hub.administer.cancel")}
        actionVariant="destructive"
        onConfirm={handleDeleteConfirm}
      />

      <ExportTokenDialog
        exportedKey={exportedKey}
        isLoading={isExportPending}
        onClose={() => {
          setExportedKey(null);
          setIsExportDialogOpen(false);
        }}
        open={isExportDialogOpen}
        title={t("hub.package_manager.export_key_title", {
          defaultValue: "Export Packages Successful",
        })}
        description={t("hub.package_manager.export_key_description", {
          defaultValue: "Please copy token key to import your packages.",
        })}
      />

      <ImportTokenDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={() => {
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          refetch();
        }}
        title={t("hub.package_manager.import_title", {
          defaultValue: "Import Packages",
        })}
        description={t("hub.package_manager.import_description", {
          defaultValue: "Paste the token key to import packages.",
        })}
      />
    </>
  );
}
